import { Database, GitBranch, Shield, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { bzDatabaseAtlasSummary } from '../data/bzDatabaseAtlasData';

interface DatabaseAtlasIntroModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DatabaseAtlasIntroModal({ isOpen, onClose }: DatabaseAtlasIntroModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  function openAtlas() {
    onClose();
    navigate('/ia-agentic/atlas-donnees-bz');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="relative max-h-[90vh] w-full max-w-4xl overflow-auto rounded-3xl bg-white shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-slate-500 shadow-md transition hover:text-slate-900"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-900 p-8 text-white">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100">
            <Database className="h-4 w-4" />
            {bzDatabaseAtlasSummary.recommendedName}
          </div>
          <h2 className="text-3xl font-black leading-tight">Cartographier les données avant de les ouvrir aux bots.</h2>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-blue-100">
            Cette page est le point de départ pour documenter les databases et tables BZ inc., clarifier les propriétaires métiers, et préparer une sécurité by-design pour les bots en production.
          </p>
        </div>

        <div className="p-8">
          <div className="grid gap-4 md:grid-cols-3">
            <Metric label="Tables détectées" value={bzDatabaseAtlasSummary.tableCount} />
            <Metric label="Domaines regroupés" value={bzDatabaseAtlasSummary.domainCount} />
            <Metric label="Relations Mermaid" value={bzDatabaseAtlasSummary.relationCount} />
          </div>

          <div className="mt-7 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <Database className="mb-3 h-6 w-6 text-blue-600" />
              <h3 className="font-black text-slate-900">Inventaire clair</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">Voir tables, domaines, colonnes et signaux de complexité à partir du MD envoyé.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <Shield className="mb-3 h-6 w-6 text-blue-600" />
              <h3 className="font-black text-slate-900">Sécurité by-design</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">Identifier les zones sensibles : clients, accès, finances, RH, sécurité et contrats.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <GitBranch className="mb-3 h-6 w-6 text-blue-600" />
              <h3 className="font-black text-slate-900">Découpage scalable</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">Préparer une évolution par domaines plutôt qu’une seule database qui porte tout.</p>
            </div>
          </div>

          <div className="mt-7 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-900">
            <strong>Résumé :</strong> le MD montre une base ERP large et historique : {bzDatabaseAtlasSummary.tableCount} tables, {bzDatabaseAtlasSummary.columnCount} colonnes et {bzDatabaseAtlasSummary.relationCount} relations. C’est documentable, mais pour les bots en production il faut graduellement passer à une cartographie par domaines, contrats d’accès et niveaux de sensibilité.
          </div>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Fermer
            </button>
            <button
              type="button"
              onClick={openAtlas}
              className="rounded-xl bg-blue-700 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-blue-700/20 transition hover:bg-blue-800"
            >
              Ouvrir l’Atlas des données BZ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
      <div className="text-2xl font-black text-slate-900">{new Intl.NumberFormat('fr-CA').format(value)}</div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
    </div>
  );
}
